/**
 * Lab Grading Details - Admin interface for viewing and analyzing grading criteria
 */

import React, { useState, useEffect } from 'react';
import { AdminService, LabGradingDetails, LabGradingCheck } from '../../services/admin';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Alert } from '../../components/ui/Alert';
import { Input } from '../../components/ui/Input';

export function LabGradingDetailsPage() {
  const [gradingDetails, setGradingDetails] = useState<LabGradingDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('');
  const [expandedLabs, setExpandedLabs] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadGradingDetails();
  }, []);

  const loadGradingDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await AdminService.getLabGradingDetails();
      setGradingDetails(data);
    } catch (err) {
      console.error('Failed to load grading details:', err);
      setError('Failed to load grading details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredLabs = gradingDetails.filter(lab => {
    const matchesSearch = !searchQuery || 
      lab.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lab.slug.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesDifficulty = !difficultyFilter || 
      lab.difficulty === difficultyFilter;

    return matchesSearch && matchesDifficulty;
  });

  const toggleLabExpanded = (labSlug: string) => {
    const newExpanded = new Set(expandedLabs);
    if (newExpanded.has(labSlug)) {
      newExpanded.delete(labSlug);
    } else {
      newExpanded.add(labSlug);
    }
    setExpandedLabs(newExpanded);
  };

  const expandAll = () => {
    setExpandedLabs(new Set(filteredLabs.map(lab => lab.slug)));
  };

  const collapseAll = () => {
    setExpandedLabs(new Set());
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCheckTypeColor = (checkType: string) => {
    switch (checkType) {
      case 'package': return 'bg-blue-100 text-blue-800';
      case 'service': return 'bg-purple-100 text-purple-800';
      case 'port': return 'bg-indigo-100 text-indigo-800';
      case 'file': return 'bg-orange-100 text-orange-800';
      case 'http': return 'bg-green-100 text-green-800';
      case 'command': return 'bg-gray-100 text-gray-800';
      case 'connectivity': return 'bg-cyan-100 text-cyan-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-900 min-h-screen">
        <div className="flex items-center space-x-2 mb-6">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Loading grading details...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2">Lab Grading Details</h1>
        <p className="text-gray-300 mb-4">
          Review grading criteria and validation checks for all labs. Use this to assess if checks are appropriate or need adjustment.
        </p>
        
        {error && (
          <Alert variant="error" className="mb-4">
            {error}
          </Alert>
        )}
      </div>

      {/* Controls */}
      <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-600 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="flex-1 min-w-64">
            <Input
              placeholder="Search labs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>
          
          <select
            value={difficultyFilter}
            onChange={(e) => setDifficultyFilter(e.target.value)}
            className="px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Difficulties</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={expandAll} size="sm">
            Expand All
          </Button>
          <Button variant="secondary" onClick={collapseAll} size="sm">
            Collapse All
          </Button>
          <div className="text-sm text-gray-300 ml-auto">
            {filteredLabs.length} lab{filteredLabs.length !== 1 ? 's' : ''} • {filteredLabs.reduce((sum, lab) => sum + lab.check_count, 0)} total checks
          </div>
        </div>
      </div>

      {/* Labs List */}
      <div className="space-y-4">
        {filteredLabs.map((lab) => {
          const isExpanded = expandedLabs.has(lab.slug);
          return (
            <div key={lab.slug} className="bg-gray-800 rounded-lg shadow-sm border border-gray-600">
              {/* Lab Header */}
              <div
                className="p-4 cursor-pointer hover:bg-gray-700 flex items-center justify-between"
                onClick={() => toggleLabExpanded(lab.slug)}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <span className="w-5 h-5 text-gray-300 text-lg">
                      {isExpanded ? '▼' : '▶'}
                    </span>
                    <h3 className="text-lg font-semibold text-white">{lab.title}</h3>
                  </div>
                  
                  <Badge className={getDifficultyColor(lab.difficulty)}>
                    {lab.difficulty}
                  </Badge>
                  
                  <span className="text-sm text-gray-300">
                    {lab.estimated_minutes} min
                  </span>
                </div>
                
                <div className="flex items-center space-x-4 text-sm text-gray-300">
                  <div className="flex items-center space-x-2">
                    <span className="w-4 h-4 text-green-500">✓</span>
                    <span>{lab.check_count} checks</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="font-semibold">{lab.total_points} points</span>
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t border-gray-600 p-4 bg-gray-700">
                  <div className="space-y-3">
                    {lab.checks.map((check, index) => (
                      <div key={check.id} className="bg-gray-800 rounded-lg border border-gray-600 p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-medium text-gray-400">#{index + 1}</span>
                              <Badge className={getCheckTypeColor(check.check_type)} size="sm">
                                {check.check_type}
                              </Badge>
                              <span className="text-sm font-semibold text-blue-600">
                                {check.points} pts
                              </span>
                            </div>
                            <h4 className="text-base font-medium text-white mb-2">
                              {check.description}
                            </h4>
                          </div>
                        </div>
                        
                        {/* Command Example */}
                        <div className="bg-gray-900 rounded-md p-3 mb-2">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="text-xs font-semibold text-gray-400 uppercase">
                              Example Command
                            </span>
                          </div>
                          <code className="text-sm text-green-400 font-mono">
                            {check.command_example}
                          </code>
                        </div>

                        {/* Configuration Details */}
                        {Object.keys(check.check_config).length > 0 && (
                          <div className="text-xs text-gray-300">
                            <span className="font-medium">Config: </span>
                            {JSON.stringify(check.check_config, null, 0)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filteredLabs.length === 0 && !loading && (
          <div className="text-center py-12">
            <span className="text-6xl text-gray-400 mx-auto mb-4 block">⚠️</span>
            <h3 className="text-lg font-medium text-white mb-2">No Labs Found</h3>
            <p className="text-gray-300">
              No labs match your current search criteria. Try adjusting your filters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}